import React, {memo, useMemo} from "react";
import PropTypes from "prop-types";
import {Chip} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import chroma from "chroma-js";
import ItemTooltip from "../forms/ItemTooltip";
import {useChipStyles} from "./chipStyles";

const SelectTagChip = memo(
  ({
    option,
    optionLabel,
    optionChipColor,
    wrapChipLabels,
    tagProps,
    overrides = {},
    fallbackDescription,
    deleteColorMode = "darken",
  }) => {
    const {classes} = useChipStyles();
    const chipColor =
      overrides.backgroundColor || option?.[optionChipColor] || option?.backgroundColor;
    const style = useMemo(
      () => ({
        backgroundColor: chipColor || "inherit",
        color:
          overrides.color ||
          option?.foregroundColor ||
          (chipColor ? chroma(chipColor).brighten(5).css() : "inherit"),
        cursor: "default",
      }),
      [chipColor, overrides.color, option?.foregroundColor],
    );
    const deleteIcon = useMemo(
      () => (
        <CancelIcon
          style={{
            color: chipColor ?
              deleteColorMode === "darken" ?
                chroma(chipColor).darken().css() :
                overrides.deleteColor || option?.foregroundColor :
              undefined,
          }}
        />
      ),
      [chipColor, deleteColorMode, overrides.deleteColor, option?.foregroundColor],
    );
    const tooltipItem = overrides.tooltip ?
      {...option, description: overrides.tooltip} :
      fallbackDescription ?
        {...option, description: fallbackDescription} :
        option;

    const chipClassName = wrapChipLabels ?
      `${classes.selectTagWrapped} ${tagProps?.className || ""}`.trim() :
      tagProps?.className;

    return (
      <span style={wrapChipLabels ? {maxWidth: "100%"} : undefined}>
        <ItemTooltip item={tooltipItem} name={option[optionLabel]}>
          <Chip
            style={style}
            deleteIcon={deleteIcon}
            variant="outlined"
            label={option[optionLabel]}
            {...tagProps}
            className={chipClassName}
            id="select-tag-chip"
          />
        </ItemTooltip>
      </span>
    );
  },
);

SelectTagChip.displayName = "SelectTagChip";

SelectTagChip.propTypes = {
  option: PropTypes.object.isRequired,
  optionLabel: PropTypes.string.isRequired,
  optionChipColor: PropTypes.string,
  wrapChipLabels: PropTypes.bool,
  tagProps: PropTypes.object,
  fallbackDescription: PropTypes.string,
  deleteColorMode: PropTypes.oneOf(["darken", "foreground"]),
  overrides: PropTypes.shape({
    backgroundColor: PropTypes.string,
    color: PropTypes.string,
    deleteColor: PropTypes.string,
    tooltip: PropTypes.string,
  }),
};

export default SelectTagChip;
